import type {
  CustomHomePost,
  CustomHomepage,
  CustomSlide,
  Endpoints,
  Offer,
  Post5,
  PostReview5,
  StarCommunity,
  User12,
  WithRoute,
} from '~/types'

import { API } from '~/utils/constants'

type HomeContent = {
  actualities: WithRoute<Post5>[]
  offers: WithRoute<Offer>[]
  waperOpinions: WithRoute<PostReview5>[]
  wapers: WithRoute<User12>[]
  medias: WithRoute<CustomHomePost>[]
  tricks: WithRoute<CustomHomePost>[]
  waperCorners: WithRoute<CustomHomePost>[]
  communities: WithRoute<StarCommunity>[]
}

type Seo = Pick<
  CustomHomepage,
  | 'title_seo'
  | 'description_seo'
  | 'keywords_seo'
  | 'title_og'
  | 'description_og'
  | 'image_og'
>

const initFormattedData = (): HomeContent => ({
  actualities: [],
  offers: [],
  waperOpinions: [],
  wapers: [],
  medias: [],
  tricks: [],
  waperCorners: [],
  communities: [],
})

// The BO lets editors put any post in any block: the route follows the post's
// own category, the block's one is only a fallback
const getPostRoute = (post: CustomHomePost, blockSlug: string) => {
  const categorySlug = post.category?.slug ?? blockSlug
  const base = `/communautes/${post.community?.slug}`

  switch (categorySlug) {
    case 'le-coin-des-wapers':
      return `${base}/le-feed-des-players/${post.sub_category?.slug ?? 'noData'}/${post.slug}`
    case 'astuces':
      return `${base}/astuces/${post.sub_category?.slug ?? 'noData'}/${post.slug}`
    default:
      return `${base}/${categorySlug}/${post.slug}`
  }
}

const getFormattedData = (rawData: CustomHomepage) => {
  if (!rawData.content_blocks) return
  const formattedData = initFormattedData()

  rawData.content_blocks.forEach((contentBlock) => {
    switch (contentBlock.content_block_type?.slug) {
      case 'actualites-communaute':
        if (!contentBlock.star_posts?.length) break

        formattedData.actualities = contentBlock.star_posts.flatMap((post) => {
          if (
            !post.post.community?.slug ||
            !contentBlock.content_block_type?.slug
          ) {
            return []
          }

          return {
            ...post.post,
            route: getPostRoute(
              post.post,
              contentBlock.content_block_type.slug,
            ),
          }
        })
        break

      case 'offres-du-moment':
        if (!contentBlock.content_block_type.offers?.length) break

        formattedData.offers = contentBlock.content_block_type.offers.flatMap(
          (offer) => {
            if (!offer.slug) return []
            return {
              ...offer,
              route: `/offres/${offer.slug}`,
            }
          },
        )
        break

      case 'avis-joueurs':
        if (
          !contentBlock.star_reviews?.length ||
          !contentBlock.content_block_type?.slug
        )
          break

        formattedData.waperOpinions = contentBlock.star_reviews.flatMap(
          (post) => {
            if (!post.review?.slug || !contentBlock.content_block_type?.slug) {
              return []
            }

            const communitySlug = post.review.community?.slug ?? 'noData'
            return {
              ...post.review,
              route: `/communautes/${communitySlug}/${contentBlock.content_block_type.slug}/detail/${post.review.slug}`,
            }
          },
        )
        break

      case 'wapers':
        if (!contentBlock.star_users?.length) break

        formattedData.wapers = contentBlock.star_users.flatMap((waper) => {
          if (!waper.user.slug) return []
          return {
            ...waper.user,
            route: `/wapers/${waper.user.slug}`,
          }
        })
        break

      case 'avis-medias':
        if (
          !contentBlock.star_posts?.length ||
          !contentBlock.content_block_type?.slug
        )
          break

        formattedData.medias = contentBlock.star_posts.flatMap((post) => {
          if (
            !contentBlock.content_block_type?.slug ||
            !post.post.slug ||
            !post.post.community?.slug
          ) {
            return []
          }

          return {
            ...post.post,
            route: getPostRoute(
              post.post,
              contentBlock.content_block_type.slug,
            ),
          }
        })
        break

      case 'astuces':
        if (!contentBlock.star_posts) break

        formattedData.tricks = contentBlock.star_posts.flatMap((post) => {
          if (
            !contentBlock.content_block_type?.slug ||
            !post.post.slug ||
            !post.post.community?.slug
          ) {
            return []
          }
          return {
            ...post.post,
            route: getPostRoute(
              post.post,
              contentBlock.content_block_type.slug,
            ),
          }
        })
        break

      case 'le-coin-des-wapers':
        if (!contentBlock.star_posts) break
        formattedData.waperCorners = contentBlock.star_posts.flatMap((post) => {
          if (
            !contentBlock.content_block_type?.slug ||
            !post.post.community?.slug ||
            !post.post.slug
          )
            return []
          return {
            ...post.post,
            route: getPostRoute(
              post.post,
              contentBlock.content_block_type.slug,
            ),
          }
        })
        break

      case 'communautes':
        if (!contentBlock.star_communities?.length) break

        formattedData.communities = contentBlock.star_communities.flatMap(
          (community) => {
            if (!community.community?.slug) return []
            return {
              ...community.community,
              route: `/communautes/${community.community.slug}`,
            }
          },
        )
        break
      default:
        break
    }
  })
  return formattedData
}

export const useHomeStore = defineStore('homeStore', () => {
  // STATE
  const slides = ref<CustomSlide[]>([])
  const homeContent = ref<HomeContent>()
  const seo = ref<Seo>()
  const connectedHomeContent = ref<HomeContent>()
  const hasCommu = ref(false)

  const { authenticated } = useAuthenticated()
  // ACTIONS
  const fetchHomeContent = async () => {
    // The page hydrates with this store from the server payload: no second request in the browser. $fetch rather
    // than useFetch: useFetch also serialized the raw answer in the HTML, next to the formatted state of the store
    // (the home data twice, ~65 KB raw, Lighthouse 06/10/2026). /homepage is public, no token needed.
    if (import.meta.client && useNuxtApp().isHydrating && homeContent.value) {
      return
    }

    let rawData: Endpoints['GET__HOMEPAGE'] | undefined
    try {
      rawData = await myFetch<Endpoints['GET__HOMEPAGE']>(API.GET__HOMEPAGE)
    } catch (error) {
      homeContent.value = undefined
      connectedHomeContent.value = undefined
      return logError(error as Error)
    }

    if (!rawData) {
      homeContent.value = undefined
      connectedHomeContent.value = undefined
      return
    }

    if (rawData.slides) {
      slides.value = rawData.slides.map((slide) => slide.slide)
    }

    const {
      title_seo,
      description_seo,
      keywords_seo,
      title_og,
      description_og,
      image_og,
    } = rawData

    seo.value = {
      title_seo,
      description_seo,
      keywords_seo,
      title_og,
      description_og,
      image_og,
    }

    const formattedData = getFormattedData(rawData)

    homeContent.value = formattedData

    if (!authenticated.value) {
      hasCommu.value = false
      return
    }
  }

  return {
    // State
    slides,
    homeContent,
    seo,
    connectedHomeContent,
    hasCommu,

    // Actions
    fetchHomeContent,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useHomeStore, import.meta.hot))
}
